import {
  collection,
  doc,
  type Firestore,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import { modelItemStream } from '../../firestore-mobx/stream'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import { SettingsDynamicIntroVideoDefaults } from '../../models/SettingsDynamicIntroVideoDefaults'
import type { FirestoreSettingsDynamicIntroVideoDefaults } from './schema'
import { empty, schema } from './schema'
import { getDocWithError, setDocWithError } from '../../firestore-mobx/fetch'

export * from './schema'

const converter: FirestoreDataConverter<FirestoreSettingsDynamicIntroVideoDefaults> =
  {
    toFirestore: (data: FirestoreSettingsDynamicIntroVideoDefaults) => data,
    fromFirestore: (snapshot: QueryDocumentSnapshot) => {
      return schema.parse(snapshot.data())
    },
  }

export const getDocRef = (firestore: Firestore) => {
  return doc(
    collection(firestore, 'settings').withConverter(converter),
    'dynamic_intro_video_defaults'
  )
}

export const getSettingsDynamicIntroVideoDefaults = (
  repository: FirebaseRepository
) => {
  const docRef = getDocRef(repository.firestore)

  return modelItemStream(repository, docRef, SettingsDynamicIntroVideoDefaults)
}

// One-shot read of the Dynamic Intro Video defaults. Returns `empty`
// ({ groupMediaId: '', soloMediaId: '' }) when the settings doc is missing or
// unreadable, so callers (e.g. the JSON importer) can proceed without it.
export const fetchSettingsDynamicIntroVideoDefaults = async (
  repository: FirebaseRepository
): Promise<FirestoreSettingsDynamicIntroVideoDefaults> => {
  const docRef = getDocRef(repository.firestore)
  const snapshot = await getDocWithError(
    docRef,
    'FetchSettingsDynamicIntroVideoDefaultsError'
  )
  const data = snapshot.data()
  return data ? schema.parse(data) : empty
}

// Partial update so the Group and Solo defaults can be set independently
// (merge: true leaves the untouched field as-is).
export const setSettingsDynamicIntroVideoDefaults = async (
  repository: FirebaseRepository,
  values: Partial<FirestoreSettingsDynamicIntroVideoDefaults>
) => {
  const docRef = getDocRef(repository.firestore)

  return setDocWithError(docRef, values, {
    merge: true,
    errorName: 'SetSettingsDynamicIntroVideoDefaultsError',
  })
}
